import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  SportsBaseball as BaseballIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { useGetTodaysScoresQuery } from "../../store/api/mlbApi";
import TeamLogo from "../TeamLogo";
import WidgetHeader from "./WidgetHeader";

/**
 * Format game time for display
 */
const formatGameTime = (gameDate) => {
  if (!gameDate) return "TBD";

  try {
    const date = new Date(gameDate);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
      hour12: true,
    });
  } catch (error) {
    return "TBD";
  }
};

/**
 * Get game status color and text
 */
const getGameStatus = (status) => {
  if (!status) return { color: "default", text: "Unknown" };

  const { abstractGameState, detailedState } = status;

  switch (abstractGameState) {
    case "Final":
      return { color: "success", text: "Final" };
    case "Live":
      return { color: "warning", text: detailedState || "Live" };
    case "Preview":
      return { color: "info", text: "Scheduled" };
    case "Postponed":
      return { color: "error", text: "Postponed" };
    default:
      return { color: "default", text: detailedState || abstractGameState };
  }
};

/**
 * Game Score Card Component
 */
const GameScoreCard = ({ game, onClick }) => {
  const { teams, gameDate, status, venue } = game;
  const gameStatus = getGameStatus(status);
  const gameTime = formatGameTime(gameDate);

  return (
    <Card
      variant="outlined"
      onClick={() => onClick(game.gamePk)}
      sx={{
        mb: 1,
        cursor: "pointer",
        "&:hover": {
          boxShadow: 2,
          transform: "translateY(-1px)",
          transition: "all 0.2s ease-in-out",
        },
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <ScheduleIcon fontSize="small" />
            {gameTime}
          </Typography>
          <Chip
            label={gameStatus.text}
            color={gameStatus.color}
            size="small"
            variant="outlined"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {/* Away Team */}
          <Box sx={{ flex: 1, textAlign: "left" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
              <TeamLogo
                mlbTeamId={teams.away.team.id}
                teamName={teams.away.team.name}
                size={22}
                alt={`${teams.away.team.name} logo`}
              />
              <Typography variant="body2" fontWeight="medium" noWrap>
                {teams.away.team.name}
              </Typography>
            </Box>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {teams.away.score || "-"}
            </Typography>
          </Box>

          {/* VS */}
          <Box sx={{ px: 2 }}>
            <Typography variant="body2" color="text.secondary">
              @
            </Typography>
          </Box>

          {/* Home Team */}
          <Box sx={{ flex: 1, textAlign: "right" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.75,
                mb: 0.25,
              }}
            >
              <Typography variant="body2" fontWeight="medium" noWrap>
                {teams.home.team.name}
              </Typography>
              <TeamLogo
                mlbTeamId={teams.home.team.id}
                teamName={teams.home.team.name}
                size={22}
                alt={`${teams.home.team.name} logo`}
              />
            </Box>
            <Typography variant="h6" color="primary" fontWeight="bold">
              {teams.home.score || "-"}
            </Typography>
          </Box>
        </Box>

        {venue && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1, textAlign: "center" }}
          >
            {venue.name}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * MLB Scores Widget Component
 */
const MLBScoresWidget = ({ config, onEdit, onDelete }) => {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);

  const {
    data: scoresData,
    isLoading,
    error,
    refetch,
  } = useGetTodaysScoresQuery();

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  }, [refetch]);

  const handleGameClick = useCallback(
    (gamePk) => {
      navigate(`/app/game/${gamePk}`);
    },
    [navigate]
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton color="inherit" size="small" onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        }
      >
        Failed to load MLB scores:{" "}
        {error?.data?.message || error?.message || "Unknown error"}
      </Alert>
    );
  }

  if (!scoresData?.success) {
    return <Alert severity="warning">No MLB scores data available</Alert>;
  }

  const {
    data: games,
    totalGames,
    gamesInProgress,
    lastUpdated,
    source,
  } = scoresData;

  return (
    <Box
      sx={{
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        borderRadius: 4,
        border: 1,
        borderColor: "divider",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <WidgetHeader
        title={config?.title || "MLB Scores"}
        onEdit={onEdit ? () => onEdit(config) : null}
        onDelete={onDelete ? () => onDelete(config) : null}
        showDragHandle={Boolean(onEdit || onDelete)}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Chip
          icon={<TrendingUpIcon />}
          label={`${gamesInProgress || 0} Live`}
          color="warning"
          size="small"
          variant="outlined"
        />
        <Tooltip title="Refresh scores">
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Summary */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          bgcolor: "background.default",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {totalGames || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Games
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {gamesInProgress || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Live Games
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {(totalGames || 0) - (gamesInProgress || 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Completed
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* Games List */}
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {games && games.length > 0 ? (
          games.map((game) => (
            <GameScoreCard key={game.gamePk} game={game} onClick={handleGameClick} />
          ))
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <BaseballIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="body1" color="text.secondary">
              No games scheduled for today
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{ mt: 2, pt: 1, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <span>
            Last updated:{" "}
            {lastUpdated
              ? new Date(lastUpdated).toLocaleTimeString()
              : "Unknown"}
          </span>
          <span>Source: {source === "cache" ? "📦 Cached" : "🔄 Fresh"}</span>
        </Typography>
      </Box>
    </Box>
  );
};

export default MLBScoresWidget;
